import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import { useTenant } from './TenantContext'
import { useLanguage } from './LanguageContext'
import { useToast } from './ToastContext'
import { useClient } from './ClientContext'
import { useCards } from './CardContext'
import apiService from '../services/api.service'

const OffersContext = createContext(null)

export const OffersProvider = ({ children }) => {
  const { tenant } = useTenant()
  const { languageCode } = useLanguage()
  const { showToast } = useToast()
  const { isAuthenticated, loading: authLoading } = useClient()
  const { cards, cardsFetched, cardsVersion } = useCards()
  const showToastRef = useRef(showToast)

  // Keep ref updated
  useEffect(() => {
    showToastRef.current = showToast
  }, [showToast])
  const [offers, setOffers] = useState([])
  const [featuredOffers, setFeaturedOffers] = useState([])
  const [offerDetails, setOfferDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingFeatured, setLoadingFeatured] = useState(false)
  const [loadingOfferDetails, setLoadingOfferDetails] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [cardNumber, setCardNumber] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const limit = 20
  const featuredLimit = 5000

  // Clear cardNumber when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setCardNumber('')
    }
  }, [isAuthenticated])

  // Initial load and reset when dependencies change.
  // Gate: wait for auth to resolve, and (if logged in) for cards to finish loading.
  // This ensures only ONE getoffers call fires per session — no card number first,
  // then card number second. Instead: not logged in → '' immediately;
  // logged in → waits for cardsFetched, then uses default card.
  useEffect(() => {
    if (authLoading) return // auth not resolved yet
    const canFetch = !!tenant?.tenantid && !!languageCode
    if (!canFetch) return
    if (isAuthenticated && !cardsFetched) return // wait for cards to load

    // Derive the card number to use:
    // - Manual card switcher (cardNumber state) takes priority
    // - Otherwise fall back to the default card from CardContext
    // - Not authenticated → always empty string
    const defaultCard = cards.find((c) => c.isdefault === 1) || cards[0]
    const effectiveCardNumber = isAuthenticated
      ? cardNumber || defaultCard?.cardnumber || ''
      : ''

    // Reset pagination when dependencies change
    setCurrentPage(0)
    setOffers([])
    setHasMore(true)

    const params = new URLSearchParams()
    params.set('tenantid', String(tenant.tenantid))
    params.set('languagecode', languageCode)
    params.set('cardnumber', effectiveCardNumber)
    params.set('page', '0')
    params.set('limit', String(limit))

    setLoading(true)
    setError(null)
    apiService
      .get(`/api/offers?${params.toString()}`)
      .then((data) => {
        const offersArray = Array.isArray(data) ? data : data?.offers || []
        setOffers(offersArray)
        setLastUpdated(Date.now())
        if (data?.totalPages) {
          setHasMore(data.totalPages > 1)
        } else {
          setHasMore(offersArray.length >= limit)
        }
      })
      .catch((e) => {
        setError(e?.message || 'Failed to load offers')
      })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenant?.tenantid,
    languageCode,
    isAuthenticated,
    authLoading,
    cardsFetched,
    cardsVersion,
    cardNumber,
  ])

  // Fetch featured offers separately (no pagination). Same auth/cards gate.
  useEffect(() => {
    if (authLoading) return
    const canFetch = !!tenant?.tenantid && !!languageCode
    if (!canFetch) return
    if (isAuthenticated && !cardsFetched) return

    // Skip entirely if the tenant has disabled featured offers
    if (tenant?.tenantmasterconfig?.enableFeaturedOffers === false) {
      setFeaturedOffers([])
      return
    }

    const defaultCard = cards.find((c) => c.isdefault === 1) || cards[0]
    const effectiveCardNumber = isAuthenticated
      ? cardNumber || defaultCard?.cardnumber || ''
      : ''

    const params = new URLSearchParams()
    params.set('tenantid', String(tenant.tenantid))
    params.set('languagecode', languageCode)
    params.set('cardnumber', effectiveCardNumber)
    params.set('featuredOffer', true)
    params.set('page', '0')
    params.set('limit', String(featuredLimit))

    setLoadingFeatured(true)
    apiService
      .get(`/api/offers?${params.toString()}`)
      .then((data) => {
        const offersArray = Array.isArray(data) ? data : data?.offers || []
        const filteredOffers = offersArray.filter(
          (offer) => offer.featuredOffer === true,
        )
        setFeaturedOffers(filteredOffers)
      })
      .catch((e) => {
        console.error('Failed to load featured offers:', e)
        setFeaturedOffers([])
      })
      .finally(() => setLoadingFeatured(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenant?.tenantid,
    tenant?.tenantmasterconfig?.enableFeaturedOffers,
    languageCode,
    isAuthenticated,
    authLoading,
    cardsFetched,
    cardsVersion,
    cardNumber,
  ])

  // Load more offers (next page)
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !tenant?.tenantid || !languageCode) return

    const nextPage = currentPage + 1
    setLoadingMore(true)
    setError(null)

    const params = new URLSearchParams()
    params.set('tenantid', String(tenant.tenantid))
    params.set('languagecode', languageCode)
    params.set('cardnumber', cardNumber || '')
    params.set('page', String(nextPage))
    params.set('limit', String(limit))

    try {
      const data = await apiService.get(`/api/offers?${params.toString()}`)
      const newOffers = Array.isArray(data) ? data : data?.offers || []

      if (newOffers.length > 0) {
        setOffers((prev) => [...prev, ...newOffers])
        setCurrentPage(nextPage)

        // Check if there are more pages
        // totalPages is 1-indexed (total count), so if totalPages = 3, we have pages 0, 1, 2
        // After loading page 2 (nextPage = 2), we check: 2 + 1 < 3 = false (no more pages)
        if (data?.totalPages) {
          setHasMore(nextPage + 1 < data.totalPages)
        } else {
          // If no totalPages in response, check if we got a full page
          setHasMore(newOffers.length >= limit)
        }
      } else {
        setHasMore(false)
      }
    } catch (e) {
      setError(e?.message || 'Failed to load more offers')
    } finally {
      setLoadingMore(false)
    }
  }, [
    loadingMore,
    hasMore,
    tenant?.tenantid,
    languageCode,
    currentPage,
    cardNumber,
    limit,
  ])

  // Fetch offer details by ID
  const fetchOfferById = useCallback(
    async (offerId) => {
      if (!offerId || !tenant?.tenantid || !languageCode) {
        setOfferDetails(null)
        return
      }

      setLoadingOfferDetails(true)
      setError(null)

      const params = new URLSearchParams()
      params.set('tenantid', String(tenant.tenantid))
      params.set('offerid', String(offerId))
      params.set('languagecode', languageCode)

      try {
        const data = await apiService.get(
          `/api/offers/offerbyid?${params.toString()}`,
        )
        // API returns { offers: [...] }, extract the first offer
        const offer =
          Array.isArray(data?.offers) && data.offers.length > 0
            ? data.offers[0]
            : Array.isArray(data) && data.length > 0
              ? data[0]
              : data
        setOfferDetails(offer || null)
      } catch (e) {
        setError(e?.message || 'Failed to load offer details')
        setOfferDetails(null)
        showToastRef.current({
          type: 'error',
          message: 'Failed to load offer details',
        })
      } finally {
        setLoadingOfferDetails(false)
      }
    },
    [tenant?.tenantid, languageCode],
  )

  const value = {
    offers,
    featuredOffers,
    offerDetails,
    loading,
    loadingMore,
    loadingFeatured,
    loadingOfferDetails,
    error,
    lastUpdated,
    cardNumber,
    setCardNumber,
    loadMore,
    hasMore,
    currentPage,
    fetchOfferById,
  }

  return (
    <OffersContext.Provider value={value}>{children}</OffersContext.Provider>
  )
}

export const useOffers = () => {
  const ctx = useContext(OffersContext)
  if (!ctx) throw new Error('useOffers must be used within OffersProvider')
  return ctx
}
